import { mat4 } from "gl-matrix";
import { IShaderProgramInfo } from "../../../SharedGL/Shader";
import { Camera } from "../../../SharedGL/Camera";
import { IRGBA } from "../../FlightTimeline/TimelineUtils";
import { ViolationBarShader } from "./shaders/ViolationBarShader";

interface IGLAttributes {
  position: WebGLBuffer;
}

export class ViolationBar {
  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;
  private buffers: IGLAttributes;
  private positions: number[];

  private modelViewMatrix: mat4;
  constructor(gl: WebGLRenderingContext, violationBarShader: ViolationBarShader) {
    // Initialize a shader program; this is where all the lighting
    // for the vertices and so forth is established.
    this.shaderProgram = violationBarShader.shaderProgram;

    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),

        yScale: gl.getUniformLocation(this.shaderProgram, "uYScale"),
        yOffset: gl.getUniformLocation(this.shaderProgram, "uYOffset"),
        rowIndex: gl.getUniformLocation(this.shaderProgram, "uRowIndex"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };

    this.modelViewMatrix = mat4.create();
    mat4.translate(
      this.modelViewMatrix, // destination matrix
      this.modelViewMatrix, // matrix to translate
      [0.0, 0.0, -5]
    ); // amount to translate

    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl);
  }

  //
  // initBuffers
  //
  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  //
  private initBuffers = (gl: WebGLRenderingContext): IGLAttributes => {
    // Create a buffer for the square's positions.
    const positionBuffer = gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.
    this.positions = [-1.0, 0.0, -1.0, 1.0, 1.0, 0.0, 1.0, 1.0];

    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.STATIC_DRAW);

    return {
      position: positionBuffer,
    };
  };

  private hexToRgb = (hex: string): number[] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16) / 255.0, parseInt(result[2], 16) / 255.0, parseInt(result[3], 16) / 255.0]
      : [1.0, 1.0, 1.0];
  };

  public draw = (
    gl: WebGLRenderingContext,
    camera: Camera,
    rowNumber: number,
    color: IRGBA,
    yScale: number,
    yOffset: number
  ): void => {
    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    {
      const numComponents = 2;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
      gl.vertexAttribPointer(
        this.programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
    }

    // Tell WebGL to use our program when drawing
    gl.useProgram(this.programInfo.program);

    // Set the shader uniforms
    const projectionMatrix = camera.getProjectionMatrix(gl);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, this.modelViewMatrix);

    gl.uniform1i(this.programInfo.uniformLocations.rowIndex, rowNumber);

    // const yOffset = rowNumber * FlightGL.aircraftRowTotalHeight;

    gl.uniform1f(this.programInfo.uniformLocations.yScale, yScale);
    gl.uniform1f(this.programInfo.uniformLocations.yOffset, yOffset);

    gl.uniform4fv(this.programInfo.uniformLocations.color, [color.red, color.green, color.blue, color.alpha]);

    {
      const offset = 0;
      gl.drawArrays(gl.TRIANGLE_STRIP, offset, 4);
    }
  };
}
